// The orbital hangar: the Brick Moon's two flyable hulls, built the way
// everything else in this world is built — chunky boxes, flat vertex colours,
// no textures, nose along +X (docs/space-game-mode-spec.md §12: "voxel craft
// models via the mergeBoxes recipe: riveted cylinders, girder trusses, bell
// nozzles").
//
// Until now the space roster borrowed the jet hangar's silhouettes, which
// meant the war over an airless moon was fought by aeroplanes: swept wings,
// tailplanes, rudders, air intakes with shock cones. Nothing out here has any
// air to bite, and a wing is a lie a player can see. These two are built from
// the vocabulary the era actually flew — a blunt crew capsule on a riveted
// service drum, gold foil blankets, RCS thruster quads at the corners, a
// radiator that glows instead of a wing that lifts, a girder truss holding a
// bell nozzle out the back, and landing legs, because on this moon setting
// down on the regolith is how you hold a relay.
//
// Their own module (the constitution's theater-isolation rule, same as
// spaceprops.js): planes.js knows nothing about them, and applyEraModels
// merges this registry in by the roster's `model` keys.

import * as THREE from "three";

/** Darken/lighten a hex colour by factor k — the flat-shade trick the rest of
 *  the hangar uses for bellies, shadowed panels, and worn trim. */
function shade(hex, k) {
  return new THREE.Color(hex).multiplyScalar(k).getHex();
}

/** Duplicate boxes across the z=0 plane (port/starboard symmetry). */
const mirrorZ = (boxes) => boxes.flatMap((b) => [b, { ...b, z: -b.z }]);

/** Duplicate boxes across the y=0 plane — RCS quads come in fours, and the
 *  four corners of a thruster ring are the same block upside down. */
const mirrorY = (boxes) => boxes.flatMap((b) => [b, { ...b, y: -b.y }]);

// --- the vacuum palette ----------------------------------------------------
// Everything a 1960s spacecraft was actually made of: crinkled gold blanket,
// bare steel, a scorched engine bell, cell-blue solar glass, and the deep
// black of an optics port that has nothing to reflect.

const foilGold = 0xd6a53a; // multilayer insulation, the era's signature crinkle
const foilShade = 0x9c7526; // the same blanket in the hull's shadow
const steel = 0x8b8f96; // unpainted structure
const girder = 0x6f7480; // truss work, darker than the skin
const gunmetal = 0x1c1e22; // gun fairings and barrel shrouds
const dark = 0x2b2f36; // shadowed detail
const glass = 0x9fd8f0; // porthole
const glassDeep = 0x6fb6d8; // the pane's shaded half
const ablative = 0x33302c; // scorched heat-shield char
const nozzle = 0xa5623a; // copper-brown engine bell, cooked by its own flame
const nozzleHot = 0x6d3a24; // the throat, cooked hotter
const cellBlue = 0x2f4f86; // solar array glass
const cellGrid = 0x1d3559; // the grid of cells across it
const radWhite = 0xd8dade; // radiator face, painted white to dump heat
const optics = 0x101216; // a telescope port: black, because space is

/**
 * The team star: the era painted a big one on everything that flew, and the
 * flank of the service drum is the only place a hull with no wings can wear
 * one. Two crossed bars and a heart — at any range a player fights at it
 * reads as the mark, and up close it is honestly three boxes.
 */
function starPlate(t, x, y, z, s) {
  const out = Math.sign(z) || 1; // stand the paint a hair proud of the skin
  return [
    { x, y, z, w: s * 1.5, h: s * 0.5, d: 0.1, color: t.accent }, // the bar
    { x, y, z, w: s * 0.5, h: s * 1.5, d: 0.1, color: t.accent }, // the upright
    { x, y, z: z + out * 0.05, w: s * 0.6, h: s * 0.6, d: 0.1, color: t.color }, // the heart
  ];
}

/**
 * An RCS quad: the little cluster of attitude thrusters at a hull corner.
 * Four of these are what actually point a spacecraft, so they are modelled
 * where the pilot's fingers move it — and they read as the reason the nose
 * swings without a rudder anywhere in sight.
 */
function rcsQuad(x, y, z, s = 1) {
  const sy = Math.sign(y) || 1;
  const sz = Math.sign(z) || 1;
  return [
    { x, y, z, w: 0.55 * s, h: 0.5 * s, d: 0.5 * s, color: steel },
    { x, y: y + sy * 0.32 * s, z, w: 0.24 * s, h: 0.2 * s, d: 0.24 * s, color: gunmetal }, // up/down jet
    { x, y, z: z + sz * 0.32 * s, w: 0.24 * s, h: 0.24 * s, d: 0.2 * s, color: gunmetal }, // side jet
    { x: x + 0.32 * s, y, z, w: 0.2 * s, h: 0.22 * s, d: 0.22 * s, color: gunmetal }, // fore jet
  ];
}

/**
 * A landing leg: strut, canted knee, footpad. Boxes don't rotate in this
 * recipe, so the cant is stair-stepped — which is exactly how the rest of
 * this world fakes a diagonal, and at the scale a leg is seen it reads as
 * the four-legged crouch of every lander the era drew.
 */
function landingLeg(x, z, { drop = 1.45, reach = 0.55, s = 1 } = {}) {
  const sz = Math.sign(z) || 1;
  return [
    { x, y: -drop * 0.35, z, w: 0.26 * s, h: drop * 0.7, d: 0.26 * s, color: girder },
    { x, y: -drop * 0.72, z: z + sz * reach * 0.5, w: 0.22 * s, h: drop * 0.42, d: 0.22 * s, color: girder },
    { x, y: -drop, z: z + sz * reach, w: 0.7 * s, h: 0.16 * s, d: 0.7 * s, color: steel }, // footpad
  ];
}

// --- DART — the interceptor -------------------------------------------------
// One crew, one gun, one tank, and nothing that isn't needed to get there and
// back. A blunt capsule with a charred cap up front, a riveted service drum
// under gold blankets, four RCS quads on the collar where the nose actually
// swings from, and a truss carrying one big bell.
//
// The two radiator blades stand UPRIGHT, on edge, port and starboard. They
// were flat panels for about an hour and every screenshot came back with an
// aeroplane in it — a horizontal slab off a fuselage is a wing to the human
// eye no matter what the comment above it says. On edge they read as what
// they are: fins of hot plumbing shining heat into the dark, and a silhouette
// nothing in the other four theaters shares. Landing legs stay down: this
// hull is expected to sit on a relay footprint and hold it.
export function dartBoxes(t) {
  return [
    // --- the crew capsule: a blunt cone, a scorched cap, a docking probe ---
    { cone: true, x: 4.35, y: 0, z: 0, len: 2.3, r0: 0.92, r1: 0.5, seg: 12, color: t.body },
    { cone: true, x: 5.65, y: 0, z: 0, len: 0.35, r0: 0.5, r1: 0.46, seg: 12, color: ablative },
    { x: 6.0, y: 0, z: 0, w: 0.45, h: 0.34, d: 0.34, color: steel }, // docking collar
    { x: 6.45, y: 0, z: 0, w: 0.55, h: 0.1, d: 0.1, color: steel }, // probe
    // The window: one small pane, deeply framed, on the pilot's left cheek —
    // the whole view of the war fits through it.
    { x: 4.5, y: 0.24, z: -0.58, w: 0.62, h: 0.5, d: 0.14, color: t.accent },
    { x: 4.5, y: 0.24, z: -0.64, w: 0.44, h: 0.34, d: 0.1, color: glass },
    { x: 4.55, y: 0.62, z: 0, w: 0.5, h: 0.16, d: 0.42, color: glassDeep }, // overhead pane
    // --- the collar: where the capsule meets the drum, and where it turns ---
    { x: 3.05, y: 0, z: 0, w: 0.26, h: 1.9, d: 1.9, color: steel },
    { x: 2.8, y: 0, z: 0, w: 0.18, h: 1.78, d: 1.78, color: t.accent },
    ...mirrorY(mirrorZ(rcsQuad(2.5, 0.76, 0.76))),
    // --- the service module: a riveted drum wrapped in gold blanket ---
    { cone: true, x: 0.55, y: 0, z: 0, len: 4.4, r0: 0.86, r1: 0.86, seg: 12, color: t.body },
    { x: 2.2, y: 0, z: 0, w: 0.26, h: 1.94, d: 1.94, color: foilGold }, // insulation bands
    { x: 0.8, y: 0, z: 0, w: 0.24, h: 1.94, d: 1.94, color: foilShade },
    { x: -0.6, y: 0, z: 0, w: 0.26, h: 1.94, d: 1.94, color: foilGold },
    { x: 0.55, y: -0.86, z: 0, w: 4.2, h: 0.2, d: 1.1, color: shade(t.body, 0.72) }, // shaded belly
    { x: 0.55, y: 0.9, z: 0, w: 3.6, h: 0.22, d: 0.7, color: shade(t.body, 1.15) }, // sunlit spine
    ...starPlate(t, 0.85, 0.15, 0.9, 0.72),
    ...starPlate(t, 0.85, 0.15, -0.9, 0.72),
    // Whip antenna and the little dish that talks to the pad.
    { x: -1.5, y: 1.25, z: 0, w: 0.09, h: 1.0, d: 0.09, color: steel },
    { x: 1.9, y: 1.12, z: 0.5, w: 0.7, h: 0.7, d: 0.14, color: radWhite },
    { x: 1.9, y: 0.95, z: 0.5, w: 0.1, h: 0.3, d: 0.1, color: steel },
    // --- the gun ring: the 23 mm on a ventral turntable under the capsule.
    // It used to be a barrel welded along the nose, which meant aiming was
    // flying; now the barrels walk (see the `turrets` entry in SPACE_MODELS)
    // and the hull below is just the seat they turn on. ---
    { x: 3.7, y: -0.72, z: 0, w: 1.5, h: 0.42, d: 1.0, color: gunmetal }, // magazine housing
    { x: 3.7, y: -0.95, z: 0, w: 1.25, h: 0.2, d: 1.25, color: dark }, // the race the ring rides
    // --- the radiator blades: upright, aft, on stub pylons. The face is
    // BARRED, not panelled — a rank of coolant pipes, so nobody mistakes it
    // for the Bastion's solar glass. ---
    ...mirrorZ([
      { x: -1.05, y: 0.15, z: 1.15, w: 0.5, h: 0.24, d: 0.9, color: girder }, // root pylon
      { x: -2.0, y: 0.3, z: 1.62, w: 2.2, h: 1.95, d: 0.2, color: t.color },
      { x: -2.0, y: 0.9, z: 1.72, w: 1.9, h: 0.2, d: 0.08, color: radWhite }, // coolant runs
      { x: -2.0, y: 0.3, z: 1.72, w: 1.9, h: 0.2, d: 0.08, color: radWhite },
      { x: -2.0, y: -0.3, z: 1.72, w: 1.9, h: 0.2, d: 0.08, color: radWhite },
      { x: -2.0, y: 1.3, z: 1.62, w: 2.1, h: 0.22, d: 0.28, color: t.accent }, // top cap
      { x: -3.05, y: 0.3, z: 1.62, w: 0.3, h: 1.8, d: 0.24, color: shade(t.color, 0.78) },
    ]),
    // --- the engine bay: truss, then bell ---
    ...mirrorZ(
      mirrorY([
        { x: -3.1, y: 0.45, z: 0.45, w: 1.5, h: 0.14, d: 0.14, color: girder },
      ]),
    ),
    { x: -3.1, y: 0, z: 0, w: 1.4, h: 0.9, d: 0.9, color: dark }, // plumbing block
    { cone: true, x: -4.35, y: 0, z: 0, len: 1.2, r0: 0.6, r1: 0.34, seg: 12, color: nozzleHot },
    { cone: true, x: -5.15, y: 0, z: 0, len: 0.7, r0: 0.95, r1: 0.6, seg: 12, color: nozzle },
    { cone: true, x: -5.52, y: 0, z: 0, len: 0.14, r0: 0.98, r1: 0.95, seg: 12, color: ablative }, // scorched lip
    // --- landing legs: four, down, always. A relay is held by the hull that
    // is standing on it. ---
    ...mirrorZ([...landingLeg(1.7, 0.95), ...landingLeg(-0.9, 0.95)]),
  ];
}

// --- BASTION — the gun corvette ---------------------------------------------
// The station that decided to fight back. A pressurised drum with an armoured
// crew capsule set into the front of it (the citadel a heavy round has to
// find), two 23 mm in cheek sponsons that fire down the same line, a girder
// spine holding two big solar arrays out where nothing can shadow them, a
// radiator bank on the spine, and twin bells on the tail. She is twice the
// Dart in every direction, she turns like the moon does, and the hull fights
// hurt — which is the whole point of a line hull.
export function bastionBoxes(t) {
  return [
    // --- the citadel: an armoured capsule, plated, with a slit to see out ---
    { cone: true, x: 5.5, y: 0, z: 0, len: 2.2, r0: 1.15, r1: 0.8, seg: 12, color: t.body },
    { x: 6.75, y: 0, z: 0, w: 0.4, h: 1.35, d: 1.35, color: ablative }, // char cap
    { x: 7.1, y: 0, z: 0, w: 0.4, h: 0.7, d: 0.7, color: steel }, // docking collar
    { x: 7.45, y: 0, z: 0, w: 0.5, h: 0.12, d: 0.12, color: steel }, // probe
    ...mirrorZ([
      { x: 5.4, y: 0.05, z: 0.92, w: 1.9, h: 1.1, d: 0.22, color: steel }, // bolted armour cheek
      { x: 5.4, y: 0.05, z: 1.02, w: 1.5, h: 0.7, d: 0.1, color: shade(steel, 0.8) },
    ]),
    { x: 5.9, y: 0.62, z: 0, w: 0.9, h: 0.3, d: 0.9, color: t.accent }, // window hood
    { x: 5.9, y: 0.5, z: 0, w: 0.7, h: 0.22, d: 0.8, color: glass },
    { x: 5.2, y: 0.85, z: 0, w: 1.1, h: 0.2, d: 0.6, color: glassDeep }, // periscope glazing
    // --- the step down to the pressure hull, with the forward RCS ring ---
    { x: 4.25, y: 0, z: 0, w: 0.4, h: 2.5, d: 2.5, color: t.accent },
    { x: 3.95, y: 0, z: 0, w: 0.24, h: 2.35, d: 2.35, color: steel },
    ...mirrorY(mirrorZ(rcsQuad(3.6, 1.05, 1.05, 1.15))),
    // --- the pressure hull: a corrugated drum, blanket-wrapped, star-painted --
    { cone: true, x: 0.1, y: 0, z: 0, len: 7.4, r0: 1.32, r1: 1.32, seg: 14, color: t.body },
    { x: 2.9, y: 0, z: 0, w: 0.28, h: 2.76, d: 2.76, color: foilGold }, // corrugation ribs
    { x: 1.6, y: 0, z: 0, w: 0.24, h: 2.76, d: 2.76, color: foilShade },
    { x: 0.3, y: 0, z: 0, w: 0.28, h: 2.76, d: 2.76, color: foilGold },
    { x: -1.0, y: 0, z: 0, w: 0.24, h: 2.76, d: 2.76, color: foilShade },
    { x: -2.3, y: 0, z: 0, w: 0.28, h: 2.76, d: 2.76, color: foilGold },
    { x: 0.1, y: -1.34, z: 0, w: 7.0, h: 0.24, d: 1.7, color: shade(t.body, 0.72) }, // shaded belly
    { x: 0.1, y: 1.36, z: 0, w: 6.2, h: 0.24, d: 1.1, color: shade(t.body, 1.15) }, // sunlit spine
    ...starPlate(t, 0.95, 0.2, 1.36, 1.05),
    ...starPlate(t, 0.95, 0.2, -1.36, 1.05),
    // --- the guns: two 23 mm on cheek RINGS, one order between them. The
    // sponsons were fixed forward once; a corvette that has to point her
    // whole hull to answer a contact is a corvette that never got to burn,
    // so the barrels turn now and the bell keeps the sky it wanted. ---
    ...mirrorZ([
      { x: 3.4, y: -0.72, z: 0.95, w: 1.6, h: 0.5, d: 1.1, color: gunmetal }, // mount housing
      { x: 3.4, y: -0.98, z: 0.95, w: 1.35, h: 0.22, d: 1.35, color: dark }, // the race
      { x: 2.3, y: -0.95, z: 0.95, w: 0.8, h: 0.8, d: 0.8, color: dark }, // ammunition can
    ]),
    // --- the solar arrays: two big flat panels out on girder booms, cell-blue
    // with the grid showing. Everything else out here runs on batteries and
    // hope; a line hull carries its own power plant. ---
    ...mirrorZ([
      { x: -0.4, y: 0.15, z: 2.2, w: 0.9, h: 0.3, d: 1.9, color: girder }, // boom
      { x: -0.4, y: 0.15, z: 3.1, w: 0.45, h: 0.45, d: 0.45, color: steel }, // gimbal
      { x: -0.4, y: 0.15, z: 5.7, w: 5.0, h: 0.16, d: 4.9, color: cellBlue },
      { x: -0.4, y: 0.24, z: 5.7, w: 4.6, h: 0.06, d: 0.28, color: cellGrid }, // cell grid
      { x: -0.4, y: 0.24, z: 4.5, w: 4.6, h: 0.06, d: 0.28, color: cellGrid },
      { x: -0.4, y: 0.24, z: 6.9, w: 4.6, h: 0.06, d: 0.28, color: cellGrid },
      { x: 2.15, y: 0.15, z: 5.7, w: 0.3, h: 0.26, d: 5.1, color: t.color }, // frame, team-painted
      { x: -2.95, y: 0.15, z: 5.7, w: 0.3, h: 0.26, d: 5.1, color: t.color },
      { x: -0.4, y: 0.15, z: 8.2, w: 5.1, h: 0.26, d: 0.3, color: t.accent }, // outboard cap
    ]),
    // --- the radiator bank on the spine, and the big dish ---
    { x: -2.2, y: 1.85, z: 0, w: 3.2, h: 0.16, d: 3.0, color: radWhite },
    { x: -2.2, y: 1.7, z: 0, w: 2.8, h: 0.16, d: 2.6, color: t.color },
    ...mirrorZ([{ x: -2.2, y: 1.55, z: 0.9, w: 0.24, h: 0.36, d: 0.24, color: girder }]),
    { x: 2.4, y: 1.95, z: 0, w: 1.5, h: 1.5, d: 0.2, color: radWhite }, // comms dish
    { x: 2.4, y: 1.55, z: 0, w: 0.14, h: 0.7, d: 0.14, color: steel },
    { x: 2.4, y: 2.35, z: 0, w: 0.2, h: 0.2, d: 0.2, color: gunmetal }, // feed horn
    { x: 3.3, y: 1.5, z: 0, w: 0.6, h: 0.5, d: 0.6, color: optics }, // gunnery telescope
    // --- the engine room: truss bay, plumbing, twin bells ---
    ...mirrorZ(
      mirrorY([{ x: -4.3, y: 0.7, z: 0.7, w: 1.9, h: 0.18, d: 0.18, color: girder }]),
    ),
    { x: -4.3, y: 0, z: 0, w: 1.8, h: 1.5, d: 1.9, color: dark },
    ...mirrorZ([
      { cone: true, x: -5.5, y: -0.05, z: 0.85, len: 1.1, r0: 0.6, r1: 0.4, seg: 10, color: nozzleHot },
      { cone: true, x: -6.35, y: -0.05, z: 0.85, len: 0.7, r0: 0.92, r1: 0.6, seg: 10, color: nozzle },
      { cone: true, x: -6.72, y: -0.05, z: 0.85, len: 0.14, r0: 0.95, r1: 0.92, seg: 10, color: ablative },
    ]),
    // Aft RCS ring: a hull this heavy needs authority at both ends.
    ...mirrorY(mirrorZ(rcsQuad(-3.3, 1.15, 1.15, 1.1))),
    // --- legs: four heavy ones. She lands badly, but she lands. ---
    ...mirrorZ([...landingLeg(2.4, 1.35, { drop: 1.5, reach: 0.7, s: 1.3 }), ...landingLeg(-1.6, 1.35, { drop: 1.5, reach: 0.7, s: 1.3 })]),
  ];
}

// --- HAMMER — the ordnance barge --------------------------------------------
// A crew pod towing a hardware store: most of her length is an open girder
// rack carrying six tungsten rods in two rows of three, because the cargo IS
// the ship. One point-defense gun on a ring mount, propellant drums slung
// under the spine, twin small bells — she is not built to fight anything she
// can see, only things two kilometres below that cannot move. The rods sit
// in daylight on purpose: an enemy who closes the range can COUNT how many
// drops the barge has left, which is exactly the kind of dread this hull is
// for.
export function hammerBoxes(t) {
  const rod = (x, z) => [
    { x, y: 0.55, z, w: 3.4, h: 0.3, d: 0.3, color: dark }, // the rod itself
    { x: x + 1.55, y: 0.55, z, w: 0.3, h: 0.36, d: 0.36, color: steel }, // guidance cap
    { x, y: 0.32, z, w: 0.5, h: 0.16, d: 0.4, color: girder }, // cradle clamp
  ];
  return [
    // --- the crew pod: a stubby cone far forward, away from the cargo ---
    { cone: true, x: 4.6, y: 0, z: 0, len: 1.8, r0: 0.85, r1: 0.5, seg: 12, color: t.body },
    { cone: true, x: 5.6, y: 0, z: 0, len: 0.3, r0: 0.5, r1: 0.45, seg: 12, color: ablative },
    { x: 4.6, y: 0.28, z: -0.55, w: 0.55, h: 0.4, d: 0.12, color: t.accent }, // frame
    { x: 4.6, y: 0.28, z: -0.6, w: 0.4, h: 0.28, d: 0.1, color: glass }, // the one window
    { x: 3.55, y: 0, z: 0, w: 0.24, h: 1.75, d: 1.75, color: steel }, // collar
    ...mirrorY(mirrorZ(rcsQuad(3.3, 0.7, 0.7))),
    // --- the spine: a long open truss (the barge is mostly daylight) ---
    ...mirrorZ(
      mirrorY([
        { x: 0.2, y: 0.62, z: 0.62, w: 6.4, h: 0.16, d: 0.16, color: girder },
      ]),
    ),
    ...mirrorZ([
      { x: 2.4, y: 0, z: 0.62, w: 0.14, h: 1.3, d: 0.14, color: girder }, // truss verticals
      { x: 0.2, y: 0, z: 0.62, w: 0.14, h: 1.3, d: 0.14, color: girder },
      { x: -1.9, y: 0, z: 0.62, w: 0.14, h: 1.3, d: 0.14, color: girder },
    ]),
    // --- the cargo: six rods, two rows of three, in the open ---
    ...rod(1.6, 0.5), ...rod(1.6, -0.5),
    ...rod(-0.3, 0.5), ...rod(-0.3, -0.5),
    ...rod(-2.2, 0.5), ...rod(-2.2, -0.5),
    // --- propellant drums slung under the spine, in gold blanket ---
    { cone: true, x: 0.3, y: -0.75, z: 0, len: 4.6, r0: 0.72, r1: 0.72, seg: 10, color: foilGold },
    { x: 1.6, y: -0.75, z: 0, w: 0.24, h: 1.62, d: 1.62, color: foilShade },
    { x: -1.0, y: -0.75, z: 0, w: 0.24, h: 1.62, d: 1.62, color: foilShade },
    ...starPlate(t, 0.3, -0.65, 0.78, 0.6),
    ...starPlate(t, 0.3, -0.65, -0.78, 0.6),
    // --- the point-defense 23 mm on a ring mount above the collar. It was
    // always drawn as a ring; now it actually turns like one. ---
    { x: 3.4, y: 0.95, z: 0, w: 0.9, h: 0.28, d: 0.9, color: steel },
    // --- the rack outriggers: radiators laid flat on the truss ends ---
    ...mirrorZ([
      { x: -0.2, y: 0.05, z: 1.55, w: 3.6, h: 0.16, d: 1.4, color: t.color },
      { x: -0.2, y: 0.12, z: 1.55, w: 3.2, h: 0.08, d: 0.16, color: radWhite },
    ]),
    // --- twin small bells off the aft truss ---
    { x: -4.2, y: 0, z: 0, w: 1.2, h: 1.1, d: 1.7, color: dark }, // thrust structure
    ...mirrorZ([
      { cone: true, x: -5.2, y: 0, z: 0.55, len: 0.8, r0: 0.62, r1: 0.36, seg: 10, color: nozzle },
      { cone: true, x: -5.62, y: 0, z: 0.55, len: 0.16, r0: 0.65, r1: 0.62, seg: 10, color: ablative },
    ]),
    // --- legs: even a barge sits down to load ---
    ...mirrorZ([...landingLeg(2.6, 0.9, { drop: 1.5 }), ...landingLeg(-3.4, 0.9, { drop: 1.5 })]),
  ];
}

// --- MULE — the tender ------------------------------------------------------
// The tank is the ship: one huge drum of propellant in gold blanket, a nose
// pod for the one crewman who owns the hoses, the biggest telescope in the
// sky on a spine mount (she spots at ranges no gun can answer), two wide
// solar wings to run the pumps, and one economical bell. Every fleet has a
// hull the whole enemy team hunts first; this is her.
export function muleBoxes(t) {
  return [
    // --- the nose pod ---
    { cone: true, x: 4.4, y: 0, z: 0, len: 1.7, r0: 0.8, r1: 0.48, seg: 12, color: t.body },
    { x: 5.35, y: 0, z: 0, w: 0.4, h: 0.32, d: 0.32, color: steel }, // hose collar
    { x: 5.75, y: 0, z: 0, w: 0.45, h: 0.1, d: 0.1, color: steel }, // probe
    { x: 4.45, y: 0.26, z: -0.5, w: 0.5, h: 0.36, d: 0.12, color: t.accent },
    { x: 4.45, y: 0.26, z: -0.55, w: 0.36, h: 0.24, d: 0.1, color: glass },
    { x: 3.45, y: 0, z: 0, w: 0.24, h: 1.7, d: 1.7, color: steel },
    ...mirrorY(mirrorZ(rcsQuad(3.2, 0.68, 0.68))),
    // --- THE TANK: a fat drum, most of the hull, all of the point ---
    { cone: true, x: 0.2, y: 0, z: 0, len: 5.6, r0: 1.25, r1: 1.25, seg: 12, color: foilGold },
    { x: 2.2, y: 0, z: 0, w: 0.3, h: 2.7, d: 2.7, color: foilShade }, // blanket seams
    { x: 0.2, y: 0, z: 0, w: 0.3, h: 2.7, d: 2.7, color: t.body },
    { x: -1.8, y: 0, z: 0, w: 0.3, h: 2.7, d: 2.7, color: foilShade },
    { x: 0.2, y: -1.28, z: 0, w: 5.2, h: 0.2, d: 1.3, color: shade(foilGold, 0.7) }, // shaded belly
    ...starPlate(t, 0.9, 0.2, 1.28, 0.7),
    ...starPlate(t, 0.9, 0.2, -1.28, 0.7),
    // --- hose reels: two drums on the flank, the tender's whole trade ---
    ...mirrorZ([
      { x: 1.5, y: -0.6, z: 1.32, w: 0.8, h: 0.8, d: 0.35, color: dark },
      { x: 1.5, y: -0.6, z: 1.52, w: 0.3, h: 0.3, d: 0.12, color: t.accent }, // coupling
    ]),
    // --- the telescope: a long black tube on the spine — her real weapon ---
    { x: 0.6, y: 1.55, z: 0, w: 0.5, h: 0.55, d: 0.55, color: steel }, // cradle
    { x: 1.4, y: 1.72, z: 0, w: 3.2, h: 0.5, d: 0.5, color: gunmetal }, // tube
    { x: 3.05, y: 1.72, z: 0, w: 0.2, h: 0.58, d: 0.58, color: optics }, // objective
    { x: -0.2, y: 1.72, z: 0, w: 0.3, h: 0.36, d: 0.36, color: dark }, // eyepiece end
    // --- one 23 mm on a chin ring: a polite objection, not an argument —
    // but a polite objection that can now look somewhere else while the
    // tender goes on tending. ---
    { x: 3.2, y: -0.72, z: 0, w: 1.2, h: 0.36, d: 0.9, color: gunmetal },
    { x: 3.2, y: -0.92, z: 0, w: 1.0, h: 0.18, d: 1.0, color: dark },
    // --- solar wings: wide, flat, cell-blue — the pumps drink sunlight ---
    ...mirrorZ([
      { x: -0.6, y: 0.2, z: 1.7, w: 0.6, h: 0.2, d: 0.9, color: girder }, // boom
      { x: -0.6, y: 0.2, z: 4.3, w: 2.6, h: 0.14, d: 4.4, color: cellBlue },
      { x: -0.6, y: 0.28, z: 4.3, w: 2.4, h: 0.06, d: 0.2, color: cellGrid },
      { x: -0.6, y: 0.28, z: 3.1, w: 0.2, h: 0.06, d: 3.9, color: cellGrid },
    ]),
    // --- one bell, economical ---
    { x: -3.2, y: 0, z: 0, w: 1.0, h: 1.0, d: 1.0, color: dark },
    { cone: true, x: -4.15, y: 0, z: 0, len: 1.0, r0: 0.55, r1: 0.32, seg: 12, color: nozzleHot },
    { cone: true, x: -4.8, y: 0, z: 0, len: 0.6, r0: 0.85, r1: 0.55, seg: 12, color: nozzle },
    // --- legs ---
    ...mirrorZ([...landingLeg(1.8, 1.0, { drop: 1.6 }), ...landingLeg(-1.6, 1.0, { drop: 1.6 })]),
  ];
}

/**
 * The registry applyEraModels merges in, keyed by each roster entry's `model`.
 * Same bundle shape the era rosters use — builder, props (never: nothing turns
 * a propeller in vacuum), the exhaust, the trail geometry, muzzles, damage
 * anchors, and where the tail stripe goes.
 *
 * `rocket: true` on the exhaust is the one new knob: a bell over-expanding
 * into vacuum has no atmosphere to stand shock diamonds in, so the plume
 * blooms wide and soft instead of spiking (see PlaneVisual).
 */
export const SPACE_MODELS = {
  dart: {
    builder: dartBoxes,
    props: [],
    jet: true, // the ear wants a thrust roar, not a piston drone
    jetpipe: { pos: [-5.6, 0, 0], rocket: true, scale: 1.0 },
    trails: { nacelles: 0.9, tip: 1.7 },
    // No fixed muzzles: the flashes belong to the ring mount below, which
    // is where the barrels actually are on any given frame.
    muzzles: [],
    turrets: [{ kind: "ring", driven: true, pos: [3.7, -1.05, 0], stowYaw: 0, stowPitch: 0, barrelLen: 1.5 }],
    damage: {
      // The engine is the bell at the back; the "wing" is the radiator pair;
      // the "canopy" is one small porthole; the "tail" is the engine truss.
      engine: { x: -4.8, y: 0, z: 0, r: 0.75 },
      wing: { x: -1.9, y: 0.35, tipZ: 1.75, chord: 1.3, thick: 0.24 },
      fuse: { x0: -2.6, x1: 3.2, y: 0.3, z: 0.88, h: 0.9 },
      canopy: { x: 4.5, y: 0.45, w: 0.9, d: 0.9 },
      tank: { x: 0.5, y: -0.8, len: 3.4 },
      tail: { x: -3.1, y: 0.5, spanZ: 0.6, finY: 1.2 },
    },
    // The squadron colour goes on the radiator blades — the only flat panels
    // on her, and the ones a wingman sees from behind.
    stripe: [
      { x: -3.2, y: 0.35, z: 1.62, w: 0.4, h: 2.1, d: 0.26 },
      { x: -3.2, y: 0.35, z: -1.62, w: 0.4, h: 2.1, d: 0.26 },
    ],
  },
  bastion: {
    builder: bastionBoxes,
    props: [],
    jet: true,
    jetpipe: { pipes: [[-6.9, -0.05, 0.85], [-6.9, -0.05, -0.85]], rocket: true, scale: 1.05 },
    trails: { nacelles: 0.85, tip: 5.9 },
    muzzles: [], // both flashes ride the cheek rings
    turrets: [
      { kind: "ring", driven: true, pos: [3.4, -1.08, 0.95], stowYaw: 0, stowPitch: 0, barrelLen: 1.8 },
      { kind: "ring", driven: true, pos: [3.4, -1.08, -0.95], stowYaw: 0, stowPitch: 0, barrelLen: 1.8 },
    ],
    damage: {
      engine: { x: -6.0, y: -0.05, z: 0.85, r: 0.9 },
      wing: { x: -0.4, y: 0.15, tipZ: 8.0, chord: 2.5, thick: 0.3 },
      fuse: { x0: -3.6, x1: 4.4, y: 0.4, z: 1.34, h: 1.4 },
      canopy: { x: 5.7, y: 0.7, w: 1.3, d: 1.1 },
      tank: { x: 0.1, y: -1.2, len: 5.2 },
      tail: { x: -2.2, y: 1.85, spanZ: 1.5, finY: 2.0 },
    },
    // No fin to stripe: the squadron colour goes on the radiator bank, which
    // is the one flat panel on her that always faces the sky.
    stripe: [{ x: -3.5, y: 1.72, z: 0, w: 0.5, h: 0.2, d: 1.4 }],
  },
  hammer: {
    builder: hammerBoxes,
    props: [],
    jet: true,
    jetpipe: { pipes: [[-5.7, 0, 0.55], [-5.7, 0, -0.55]], rocket: true, scale: 0.8 },
    trails: { nacelles: 0.8, tip: 1.7 },
    muzzles: [],
    turrets: [{ kind: "ring", driven: true, pos: [3.4, 1.15, 0], stowYaw: 0, stowPitch: 0, barrelLen: 1.2 }],
    damage: {
      engine: { x: -5.0, y: 0, z: 0.55, r: 0.7 },
      wing: { x: -0.2, y: 0.05, tipZ: 2.25, chord: 3.6, thick: 0.2 },
      fuse: { x0: -3.4, x1: 3.2, y: 0.2, z: 0.9, h: 1.4 },
      canopy: { x: 4.6, y: 0.3, w: 0.9, d: 0.9 },
      tank: { x: 0.3, y: -0.75, len: 4.6 },
      tail: { x: -4.2, y: 0.4, spanZ: 0.9, finY: 1.0 },
    },
    // The colour goes on the flat radiator outriggers beside the rack.
    stripe: [
      { x: -1.9, y: 0.05, z: 1.55, w: 0.5, h: 0.2, d: 1.4 },
      { x: -1.9, y: 0.05, z: -1.55, w: 0.5, h: 0.2, d: 1.4 },
    ],
  },
  mule: {
    builder: muleBoxes,
    props: [],
    jet: true,
    jetpipe: { pos: [-5.1, 0, 0], rocket: true, scale: 0.85 },
    trails: { nacelles: 0.8, tip: 6.4 },
    muzzles: [],
    turrets: [{ kind: "ring", driven: true, pos: [3.2, -1.02, 0], stowYaw: 0, stowPitch: 0, barrelLen: 1.1 }],
    damage: {
      engine: { x: -4.4, y: 0, z: 0, r: 0.7 },
      wing: { x: -0.6, y: 0.2, tipZ: 6.4, chord: 2.6, thick: 0.2 },
      fuse: { x0: -2.6, x1: 3.4, y: 0.3, z: 1.3, h: 1.5 },
      canopy: { x: 4.45, y: 0.4, w: 0.8, d: 0.8 },
      tank: { x: 0.2, y: -1.0, len: 5.4 },
      tail: { x: -3.2, y: 0.5, spanZ: 0.7, finY: 1.1 },
    },
    // Her stripe rides the telescope cradle — the mark of the trade.
    stripe: [{ x: 0.6, y: 1.55, z: 0, w: 0.6, h: 0.6, d: 0.7 }],
  },
};
